'use strict';

import React from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import moment from "moment";

import {FaExclamationTriangle} from "react-icons/fa";

import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";

import appStore from "@uw-it-sis/lib-react/lib/AppStore";
import {normalize} from "@uw-it-sis/lib-react-myplan/lib/CourseUtils";
import {isEmpty, isFalse, isTrue} from "@uw-it-sis/lib-react/lib/Utils";
import RegistrationDateStore from "@uw-it-sis/lib-react-myplan/lib/RegistrationDateStore";
import HelpPopover from "@uw-it-sis/lib-react/lib/HelpPopover";

import PlanItemActions from "./PlanItemActions";
import CreditBadge from "./CreditBadge";
import RegistrationConstants from "../registration/RegistrationConstants";
import PlanItemSectionList from "./PlanItemSectionList";
import PlanItemFunctionConstants from "./PlanItemFunctionConstants";
import planItemModalStore from "../registration/PlanItemModalStore";
import {DateFormatString, PlanItemType, PlanViewType} from "../lib/Constants";

/**
 * Component for displaying a plan item course
 */
const PlanItemCourse = ({item = {}, type, viewType = 'planView', readOnly = false, sendToRegistration = true, openForPlanning = true, isPublished = false}) => {
    if ( isEmpty(item) ) {
        // This shouldn't happen, but just in case
        return null;
    }

    let planItem = item;

    let course = planItem.courseDetails;

    let courseCode = normalize(course.code);

    let isOpenForPlanning = openForPlanning;

    // Display a warning if there are no sections available for the term.
    let courseWarning;
    if ( viewType === PlanViewType.plan && planItem.statusAlerts.length > 0 && isOpenForPlanning ) {
        courseWarning =
            <HelpPopover icon={FaExclamationTriangle}
                         title="Course Issues"
                         placement="top"
                         className="ms-3 align-middle text-warning">
                {planItem.statusAlerts.join(" ").replace(/\s+/g, ' ')}
            </HelpPopover>;
    }

    let cdpLink = `/course/#/courses/${courseCode}?id=${course.courseId}`;
    let courseCodeLink =
        <span className="align-middle lead">
            <strong>{courseCode}</strong>
            <span className="sr-only">
                {course.courseTitle}
            </span>
        </span>;

    let courseTitleLink;

    if (courseCode && !isEmpty(course.courseId)) {
        courseCodeLink =
            <a className="align-middle lead"
               href={cdpLink}>
                <strong>{courseCode}</strong>
                <span className="sr-only">
                    {course.courseTitle}
                </span>
            </a>;

        courseTitleLink =
            <a className="d-block me-1 text-truncate"
               href={cdpLink}
               title={course.courseTitle}
               aria-hidden={true}
               tabIndex={-1}>
                {course.courseTitle}
            </a>;
    }

    const toggleNote = (e) => {
        e.target.classList.toggle("text-truncate");
    };

    // Display the plan item note when it has a value and the view is a plan view
    let noteBody;
    if ( viewType === PlanViewType.plan && type !== PlanItemType.recommended && planItem.note && planItem.note !== "" ) {
        noteBody =
            <Card.Body className="pt-0 px-3 pb-2">
                <p tabIndex={0}
                   className="PlanItemNote fst-italic mb-0 text-truncate"
                   style={{cursor: "pointer"}}
                   onFocus={(e) => toggleNote(e)}
                   onBlur={(e) => toggleNote(e)}
                   title={planItem.note}>
                    {planItem.note}
                </p>
            </Card.Body>
    }

    // Open the course summary modal
    const showModal =() => {
        planItemModalStore.openModal('accept', planItem, {context: "PlanView-modal"});
    };

    // Build dropdown component for plan item actions
    let planItemActions =
        <PlanItemActions title={courseCode}
                         sectionsAvailable={planItem.sectionsAvailable}
                         data={planItem}
                         type={type}
                         openForPlanning={openForPlanning}
                         isPublished={isPublished}
                         isBlock={planItem.plannedBlockRegistration} />;

    // Display a message when it is of type recommended and the view is a plan view
    let recommendedBody = null;
    if ( viewType === PlanViewType.plan && type === PlanItemType.recommended) {
        // If the plan item is a recommendation, and the recommendation isn't already on the plan,
        // show the accept recommendation link if the user is not an adviser
        let recommendedLink = null;
        if ( ! appStore.isAdviser() ) {
            if ( isFalse(planItem.recommendationOnPlan) && openForPlanning ) {
                recommendedLink =
                    <Button variant="inline-link"
                            onClick={showModal}
                            aria-haspopup="true"
                            className="text-decoration-underline mt-3">
                        Add the recommendation to plan
                    </Button>;
            } else {
                // The only action item that would be in the PlanItemActions for a recommended item ( for now )
                // is to accept the recommendation
                // If the link is not going to be displayed to accept the recommendation, do not provide the actions,
                // since the only action to do is the one you are trying to hide
                planItemActions = null;
            }
        }

        let recommendNote = null;
        if ( planItem.note && planItem.note !== "" ) {
            // Single quotes around adviser note
            recommendNote =
                <p tabIndex={0}
                   className="PlanItemNote fst-italic mt-3 mb-0 text-truncate"
                   style={{cursor: "pointer"}}
                   onFocus={(e) => toggleNote(e)}
                   onBlur={(e) => toggleNote(e)}
                   title={planItem.note}>
                    '{planItem.note}'
                </p>;
        }

        recommendedBody =
            <Card.Body className="pt-0 px-3 pb-2">
                <p className="mb-0 lh-sm">
                    <small>{item.adviserName} recommended this on {moment(item.planItemDataObject.dateAdded).format(DateFormatString)}</small>
                </p>
                {recommendNote}
                {recommendedLink}
            </Card.Body>
    }

    // Build a card body for course validation issues
    let issuesBody;
    if ( viewType === PlanViewType.reg && planItem.validationIssues && !isEmpty(planItem.validationIssues) ) {
        /**
         * Check if there are too many primary section added issues. This is being done since the map of issues from the
         * back end does not guarantee order and we need the primary section issues to be displayed before the secondary
         * section issues
         */
        let primaryMsgCode = [
            RegistrationConstants.PrimarySectionActionCodes.REMOVE_ALL_BUT_ONE_SECTION,
            RegistrationConstants.PrimarySectionActionCodes.REMOVE_ALL_BUT_ONE_REQUIRED_SECTION,
            RegistrationConstants.PrimarySectionActionCodes.CHECK_WITH_DEPARTMENT
        ];

        let analyticsData = {
            hasIssues: 'true',
            hasSections: planItem.planActivities.length > 0,
            timeSchPublished: 'true',
            cCode: courseCode,
            planItemType: 'course'
        };

        let courseIssuesList = [];
        for ( let actionCode of primaryMsgCode ) {
            if (planItem.validationIssues[actionCode] !== undefined) {
                courseIssuesList.push(
                    PlanItemFunctionConstants.makeMessageComponent(actionCode, planItem, analyticsData)
                );
                planItem.validationIssues[actionCode] = undefined;
            }
        }

        for ( const errorCode in planItem.validationIssues ) {
            if (planItem.validationIssues[errorCode] !== undefined){
                courseIssuesList.push(
                    PlanItemFunctionConstants.makeMessageComponent(errorCode, planItem, analyticsData)
                )
            }
        }

        let isNowBetweenP1RegAndLastAddDate = (
            RegistrationDateStore.isNowAfterPeriodOnePriorityRegDate(planItem.planItemDataObject.atp, 7)
            &&
            RegistrationDateStore.isNowBeforeLastAddDate(planItem.planItemDataObject.atp)
        );

        let errorClasses = classnames({"text-danger": isNowBetweenP1RegAndLastAddDate});

        issuesBody =
            <Card.Body className="d-flex align-items-start pt-0 px-3 pb-2">
                <FaExclamationTriangle className="me-2 mt-1 text-warning" aria-hidden={true} />
                <div className={errorClasses}>
                    {courseIssuesList}
                </div>
            </Card.Body>
    }

    // Build a card body for the sections of a course, but only for current and future terms (published)
    let sectionsBody;
    if ( planItem.planActivities.length > 0 && isOpenForPlanning ) {
        sectionsBody =
            <Card.Body className="pt-0 px-3 pb-2">
                <PlanItemSectionList planItem={planItem} viewType={viewType} sendToRegistration={sendToRegistration} />
            </Card.Body>
    }

    // Create checkbox for send to registration, only available on reg view and not read only
    // Read only would be courses with issues or part of a block reg that are not the parent of the block reg
    let courseSelector;
    if ( isFalse(readOnly) && viewType === PlanViewType.reg ) {
        courseSelector =
            <Form.Group className="mx-3 mb-0">
                <Form.Check id={"include-" + planItem.planItemDataObject.id}
                            type="checkbox"
                            className="me-0"
                            inline>
                    <Form.Check.Label className="sr-only">Include this course with registration</Form.Check.Label>
                    <Form.Check.Input type="checkbox"
                                      className="me-0 fs-5"
                                      checked={sendToRegistration}
                                      title="Include this course with registration"
                                      onChange={PlanItemFunctionConstants.onFormChange}
                                      value={planItem.planItemDataObject.id}
                                      disabled={appStore.isAdviser()} />
                </Form.Check>
            </Form.Group>;
    }

    // Build element for course grade for past terms.
    let courseGrade;
    if ( isFalse(isOpenForPlanning) && course.grade ) {
        courseGrade = <span className="me-1 text-muted">{course.grade}</span>
    }

    return (
        <Card border="light-gray">
            <Card.Header className="d-flex justify-content-between align-items-start bg-transparent py-2 ps-3 pe-1">
                <div style={{minWidth: "0"}}>
                    {courseCodeLink}
                    <CreditBadge credit={course.credit} className="ms-3"/>
                    {courseWarning}
                    {courseTitleLink}
                </div>
                {/*{courseSelector}*/}
                {isFalse(isOpenForPlanning) && type === PlanItemType.registered ? courseGrade : planItemActions}
            </Card.Header>
            {recommendedBody}
            {noteBody}
            {issuesBody}
            {sectionsBody}
        </Card>
    );
}

PlanItemCourse.propTypes = {
    item: PropTypes.object.isRequired, // the plan item object (not to be confused with planItemDataObject which the item contains)
    type: PropTypes.oneOf(Object.keys(PlanItemType)).isRequired, // the type of plan item (planned, backup, registered, recommended)
    viewType: PropTypes.oneOf(Object.keys(PlanViewType)), // which view is displaying the component, regView or planView
    readOnly: PropTypes.bool, // plan item either has issues that prevent registration or is a child of block reg
    sendToRegistration: PropTypes.bool, // whether the plan item is selected/deselected for sending to registration
    openForPlanning: PropTypes.bool.isRequired,  // whether term is open for planning
    isPublished: PropTypes.bool.isRequired // whether term is published (has section data)
};



export default PlanItemCourse;